import { useState } from 'react';
import { useParams, Link, Outlet } from 'react-router-dom';
import { OctagonX, EllipsisVertical, Trash, Check, X } from 'lucide-react';

import { useCoachActivity } from '@/data/coachActivity';

import Loader from '@/components/common/Loader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import ApprovedCoachContractMessage from './components/ApprovedCoachContractMessage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import ApproveDialog from './components/ApproveDialog';
import RejectDialog from './components/RejectDialog';
import UnApproveDialog from './components/UnApproveDialog';
import { COACH_ACTIVITY_STATUS } from './enums';
import { useApplication } from '@/data/application';

const Tab = () => {
  const params = useParams();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isUnapproveDialogOpen, setIsUnapproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const application = useApplication(params.coachActivityId);

  const getBadgeVariant = (status) => {
    switch (status) {
      case COACH_ACTIVITY_STATUS.PENDING:
        return 'outline';
      case COACH_ACTIVITY_STATUS.APPROVED:
        return 'secondary';
      case COACH_ACTIVITY_STATUS.REJECTED:
        return 'destructive';
      case COACH_ACTIVITY_STATUS.SUBMITTED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">
              {coachActivity.data.activity.title}
            </h2>
            <Badge variant={getBadgeVariant(coachActivity.data.status)}>
              {coachActivity.data.status}
            </Badge>
          </div>
          <div className="flex gap-2">
            {coachActivity.data.status === COACH_ACTIVITY_STATUS.SUBMITTED && (
              <>
                <Button
                  onClick={() => setIsApproveDialogOpen(true)}
                  variant="success"
                  disabled={
                    coachActivity.data.status !==
                    COACH_ACTIVITY_STATUS.SUBMITTED
                  }
                >
                  <Check />
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsRejectDialogOpen(true)}
                  disabled={
                    coachActivity.data.status !==
                    COACH_ACTIVITY_STATUS.SUBMITTED
                  }
                >
                  <X />
                  Reject
                </Button>
              </>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Open menu"
                  className="[&>svg]:m-0"
                >
                  <EllipsisVertical size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setIsUnapproveDialogOpen(true)}
                  disabled={
                    coachActivity.data.status !== COACH_ACTIVITY_STATUS.APPROVED
                  }
                >
                  <Trash />
                  Un-Approve Activity
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <ApprovedCoachContractMessage />
        {coachActivity.data.rejectionMessage && (
          <Alert variant="destructive" className="mb-6">
            <OctagonX className="h-4 w-4" />
            <AlertTitle>Rejection Message</AlertTitle>
            <AlertDescription>
              {coachActivity.data.rejectionMessage}
            </AlertDescription>
          </Alert>
        )}
        <div className="flex gap-4">
          <div className="flex flex-col space-y-2 w-[400px] pr-4 border-r">
            {application.data.steps.map((step) => (
              <Link
                key={step.key}
                to={`/coach/${params.id}/activities/${params.coachActivityId}/${step.key}`}
                className={`px-4 py-2 rounded-lg hover:bg-muted transition-colors flex gap-2 ${
                  step.key === params.stepKey ? 'bg-muted' : ''
                }`}
              >
                <div
                  className={`w-8 h-8 ${
                    step.isComplete ? 'bg-green-500' : 'bg-primary'
                  } rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  {step.isComplete ? (
                    <Check size={16} />
                  ) : (
                    <span className="text-white font-bold">{step.step}</span>
                  )}
                </div>
                <div className="ml-4 flex-grow">
                  <h3 className="font-semibold text-lg">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">
                    {step.subTitle}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <div className="flex-1">
            <Outlet />
          </div>
        </div>
      </div>
      {isApproveDialogOpen && (
        <ApproveDialog
          coachActivityId={params.coachActivityId}
          setIsOpen={setIsApproveDialogOpen}
        />
      )}
      {isUnapproveDialogOpen && (
        <UnApproveDialog
          coachActivityId={params.coachActivityId}
          setIsOpen={setIsUnapproveDialogOpen}
        />
      )}
      {isRejectDialogOpen && (
        <RejectDialog
          coachActivityId={params.coachActivityId}
          setIsOpen={setIsRejectDialogOpen}
        />
      )}
    </>
  );
};

const TabLoader = () => {
  const params = useParams();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const application = useApplication(params.coachActivityId);

  if (coachActivity.isLoading && !coachActivity.data) {
    return (
      <div className="h-32 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (application.isLoading && !application.data) {
    return (
      <div className="h-32 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return <Tab />;
};

export default TabLoader;
